// frontend/src/pages/MatchScoring/EditBallModal.js
//
// Tap any ball in the BallsStrip → opens this modal pre-filled with
// that ball's data. Save → calls PATCH /scorer/balls/:ballId.
//
// SCOPE OF EDITS (intentionally limited):
//   - Off-the-bat runs (0..6)            — most common edit
//   - Dismissal type (or remove dismissal) + fielder
//   - Wide / no-ball runs total
//   - Bye / leg-bye runs
//   - Free-text correction reason
//
// NOT supported via this modal — scorer should Undo + re-enter:
//   - Changing kind (legal ↔ wide ↔ no-ball ↔ bye ↔ legbye)
//   - Changing zone/shot/incident (rare; can be handled later)
//
// `ball` is a row from state.recent_balls (or fetched fresh via
// GET /balls/:ballId for older balls).
//
// On save, parent calls refresh().

import React, { useEffect, useMemo, useState } from 'react';

const DISMISSAL_OPTIONS = [
  { v: '',           label: '— no wicket —' },
  { v: 'bowled',     label: 'Bowled',      needsFielder: false },
  { v: 'caught',     label: 'Caught',      needsFielder: true  },
  { v: 'lbw',        label: 'LBW',         needsFielder: false },
  { v: 'run_out',    label: 'Run out',     needsFielder: true  },
  { v: 'stumped',    label: 'Stumped',     needsFielder: true  },
  { v: 'hit_wicket', label: 'Hit wicket',  needsFielder: false },
  { v: 'caught_behind', label: 'Caught behind', needsFielder: true },
  { v: 'retired_out',label: 'Retired out', needsFielder: false },
];

function classifyKind(b) {
  if (parseInt(b.wides, 10)   > 0) return 'wd';
  if (parseInt(b.noballs, 10) > 0) return 'nb';
  if (parseInt(b.byes, 10)    > 0) return 'b';
  if (parseInt(b.legbyes, 10) > 0) return 'lb';
  return 'bat';
}

export default function EditBallModal({
  ball,                    // ball row from state.recent_balls
  bowlingSquad,
  battingSquad,
  membersById,
  onCancel, onSave,        // onSave(payload) — parent calls API
  busy,
}) {
  const kind = useMemo(() => classifyKind(ball), [ball]);

  // Initial values pulled from the ball
  const [runsOffBat, setRunsOffBat] = useState(parseInt(ball.runs_off_bat || 0, 10));
  const [extraRuns,  setExtraRuns]  = useState(() => {
    if (kind === 'wd') return parseInt(ball.wides   || 0, 10);
    if (kind === 'nb') return parseInt(ball.noballs || 0, 10) + parseInt(ball.runs_off_bat || 0, 10) + parseInt(ball.byes || 0, 10) + parseInt(ball.legbyes || 0, 10);
    if (kind === 'b')  return parseInt(ball.byes    || 0, 10);
    if (kind === 'lb') return parseInt(ball.legbyes || 0, 10);
    return 0;
  });
  const [dismissalType, setDt]      = useState(ball.dismissal_type || '');
  const [bowlerId,      setBowlerId] = useState(ball.bowler_id || '');
  const [dismissedId,   setDi]      = useState(ball.dismissed_player_id || '');
  const [fielderId,     setFi]      = useState(ball.dismissal_fielder_id || '');
  // Fielding incident on this ball (drop / missed run-out / misfield). Most often
  // a dropped catch on a dot ball — recordable here without it being a wicket.
  const [incident,      setIncident] = useState(ball.incident || '');
  const [incidentPlayerId, setIncidentPlayer] = useState(ball.incident_player_id || '');
  const [correctionReason, setReason] = useState('');

  // Re-sync every field whenever a DIFFERENT ball is opened. Without this the
  // modal keeps the previously-edited ball's values if React reuses the
  // instance (title reads the prop directly, so it looked right while the
  // body showed stale data).
  useEffect(() => {
    const k = classifyKind(ball);
    setRunsOffBat(parseInt(ball.runs_off_bat || 0, 10));
    setExtraRuns(
      k === 'wd' ? parseInt(ball.wides || 0, 10) :
      k === 'nb' ? parseInt(ball.noballs || 0, 10) + parseInt(ball.runs_off_bat || 0, 10) + parseInt(ball.byes || 0, 10) + parseInt(ball.legbyes || 0, 10) :
      k === 'b'  ? parseInt(ball.byes || 0, 10) :
      k === 'lb' ? parseInt(ball.legbyes || 0, 10) : 0
    );
    setDt(ball.dismissal_type || '');
    setDi(ball.dismissed_player_id || '');
    setFi(ball.dismissal_fielder_id || '');
    setIncident(ball.incident || '');
    setIncidentPlayer(ball.incident_player_id || '');
    setBowlerId(ball.bowler_id || '');
    setReason('');
  }, [ball]);

  const dt = DISMISSAL_OPTIONS.find(d => d.v === dismissalType) || DISMISSAL_OPTIONS[0];
  const needsFielder = dt.needsFielder;

  const nameOf = (id) => membersById?.get(id)?.display_name || (id ? id.slice(0, 8) : '');

  // Available "out batter" choices: striker + non-striker on this ball
  const outBatterChoices = useMemo(() => {
    const out = [];
    if (ball.striker_id)     out.push({ id: ball.striker_id,     label: nameOf(ball.striker_id) + ' *' });
    if (ball.non_striker_id) out.push({ id: ball.non_striker_id, label: nameOf(ball.non_striker_id) });
    return out;
  }, [ball, membersById]);

  function buildPayload() {
    // Keep the commentary's bowler credit in sync with the selected bowler.
    // Find any bowling-squad member named in the "off {name}" slot that isn't
    // the selected bowler and replace it. This runs on EVERY save, so it also
    // repairs balls whose bowler was corrected before this logic existed —
    // just re-open the ball and save.
    let commentaryText = ball.commentary_text;
    const selBowlerName = membersById?.get(bowlerId || ball.bowler_id)?.display_name;
    if (commentaryText && selBowlerName) {
      (bowlingSquad || []).forEach(s => {
        const nm = membersById?.get(s.member_id)?.display_name;
        if (nm && nm !== selBowlerName && commentaryText.includes(`off ${nm}`)) {
          commentaryText = commentaryText.split(`off ${nm}`).join(`off ${selBowlerName}`);
        }
      });
    }
    // Carry forward most fields from the original ball; override only the editable ones.
    const payload = {
      // Carry forward
      striker_id:           ball.striker_id,
      non_striker_id:       ball.non_striker_id,
      bowler_id:            bowlerId || ball.bowler_id,
      is_legal:             ball.is_legal,
      is_boundary:          ball.is_boundary,
      boundary_value:       ball.boundary_value,
      wide_underlying:      ball.wide_underlying,
      noball_source:        ball.noball_source,
      is_free_hit:          ball.is_free_hit,
      triggers_free_hit:    ball.triggers_free_hit,
      zone_sector:          ball.zone_sector,
      zone_ring:            ball.zone_ring,
      shot_type:            ball.shot_type,
      fielder_id:           ball.fielder_id,
      incident:             incident || null,
      incident_player_id:   incident ? (incidentPlayerId || null) : null,
      commentary_overridden: ball.commentary_overridden,
      commentary_text:      commentaryText,
      // Atomic counts (override per kind)
      runs_off_bat: 0,
      wides:        0,
      noballs:      0,
      byes:         0,
      legbyes:      0,
      // Dismissal (override)
      dismissal_type:        dismissalType || null,
      dismissed_player_id:   dismissalType ? (dismissedId || ball.striker_id) : null,
      dismissal_fielder_id:  dismissalType && needsFielder ? (fielderId || null) : null,
      dismissal_fielder2_id: ball.dismissal_fielder2_id,
      // DRS (carry forward)
      drs_taken_by:          ball.drs_taken_by,
      drs_original_decision: ball.drs_original_decision,
      drs_decision:          ball.drs_decision,
      // Audit
      correction_reason:     correctionReason || null,
    };

    if (kind === 'bat') {
      payload.runs_off_bat = Math.max(0, parseInt(runsOffBat, 10) || 0);
    } else if (kind === 'wd') {
      payload.wides = Math.max(1, parseInt(extraRuns, 10) || 1);
    } else if (kind === 'nb') {
      payload.noballs = 1;
      payload.runs_off_bat = Math.max(0, parseInt(runsOffBat, 10) || 0);
      // For nb, extraRuns kept simple — only off-the-bat runs are editable here
    } else if (kind === 'b') {
      payload.byes = Math.max(1, parseInt(extraRuns, 10) || 1);
    } else if (kind === 'lb') {
      payload.legbyes = Math.max(1, parseInt(extraRuns, 10) || 1);
    }
    return payload;
  }

  function handleSave() {
    if (busy) return;
    onSave(buildPayload());
  }

  // What "kind" label to show
  const kindLabel = ({
    bat: 'Off-the-bat ball',
    wd:  'Wide',
    nb:  'No-ball',
    b:   'Bye',
    lb:  'Leg-bye',
  })[kind] || 'Ball';

  return (
    <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="sc-modal">
        <div className="sc-modal-body">
          <h3>Edit ball — {ball.display_label || `${ball.over_number}.${ball.ball_in_over}`}</h3>
          <div style={{ fontSize: 11, color: 'var(--sc-text-mute)', marginTop: -8, marginBottom: 12 }}>
            {kindLabel} · originally {ball.total_runs}r{ball.dismissal_type ? ' + W' : ''}
            <br/>
            <em>Tip: to change between wide / no-ball / bye / leg-bye, undo this ball and re-enter.</em>
          </div>

          {/* Bowler — lets a mis-attributed delivery be reassigned */}
          <h4>Bowler</h4>
          <div className="sc-chip-row">
            {(bowlingSquad || []).filter(s => !s.is_sub).map(s => (
              <button
                key={s.member_id}
                className={`sc-chip ${bowlerId === s.member_id ? 'sc-active' : ''}`}
                onClick={() => setBowlerId(s.member_id)}
              >
                {nameOf(s.member_id)}
              </button>
            ))}
          </div>

          {/* Runs section */}
          {kind === 'bat' && (
            <>
              <h4>Runs off the bat</h4>
              <div className="sc-chip-row">
                {[0,1,2,3,4,5,6].map(n => (
                  <button
                    key={n}
                    className={`sc-chip ${runsOffBat === n ? 'sc-active' : ''}`}
                    onClick={() => setRunsOffBat(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </>
          )}

          {kind === 'wd' && (
            <>
              <h4>Wide — total runs (incl. 1 wide penalty)</h4>
              <div className="sc-chip-row">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    className={`sc-chip ${extraRuns === n ? 'sc-active' : ''}`}
                    onClick={() => setExtraRuns(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </>
          )}

          {kind === 'nb' && (
            <>
              <h4>No-ball — runs off the bat</h4>
              <div className="sc-chip-row">
                {[0,1,2,3,4,6].map(n => (
                  <button
                    key={n}
                    className={`sc-chip ${runsOffBat === n ? 'sc-active' : ''}`}
                    onClick={() => setRunsOffBat(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--sc-text-mute)', marginTop: 4 }}>
                No-ball penalty (1) preserved automatically.
              </div>
            </>
          )}

          {(kind === 'b' || kind === 'lb') && (
            <>
              <h4>{kind === 'b' ? 'Bye' : 'Leg-bye'} — runs</h4>
              <div className="sc-chip-row">
                {[1,2,3,4].map(n => (
                  <button
                    key={n}
                    className={`sc-chip ${extraRuns === n ? 'sc-active' : ''}`}
                    onClick={() => setExtraRuns(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Dismissal section */}
          <h4>Dismissal</h4>
          <div className="sc-chip-row">
            {DISMISSAL_OPTIONS.map(d => (
              <button
                key={d.v || 'none'}
                className={`sc-chip ${dismissalType === d.v ? 'sc-active' : ''} ${d.v ? 'sc-danger' : ''}`}
                onClick={() => { setDt(d.v); if (!d.v) { setDi(''); setFi(''); } }}
              >
                {d.label}
              </button>
            ))}
          </div>

          {dismissalType && (
            <>
              <h4>Out batter</h4>
              <div className="sc-chip-row">
                {outBatterChoices.map(c => (
                  <button
                    key={c.id}
                    className={`sc-chip ${dismissedId === c.id ? 'sc-active' : ''}`}
                    onClick={() => setDi(c.id)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {dismissalType && needsFielder && (
            <>
              <h4>Fielder {dismissalType === 'stumped' || dismissalType === 'caught_behind' ? '(keeper)' : ''}</h4>
              <div className="sc-chip-row">
                <button
                  className={`sc-chip ${fielderId === '' ? 'sc-active' : ''}`}
                  onClick={() => setFi('')}
                >
                  — none —
                </button>
                {(bowlingSquad || [])
                  /* sub can take a catch / run-out, but only keep wicket if can_keep */
                  .filter(s => !((dismissalType === 'stumped' || dismissalType === 'caught_behind') && s.is_sub && !s.can_keep))
                  .map(s => (
                  <button
                    key={s.member_id}
                    className={`sc-chip ${fielderId === s.member_id ? 'sc-active' : ''}`}
                    onClick={() => setFi(s.member_id)}
                  >
                    {nameOf(s.member_id)}{s.is_sub ? ' (sub)' : ''}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Fielding incident (drop / missed run-out / misfield) — works on any
              ball including a dot. Not a wicket; credited to the fielder for
              fielding records (drops). */}
          <h4>Fielding incident (optional)</h4>
          <div className="sc-chip-row">
            {[
              { v: '',              label: '— none —' },
              { v: 'drop',          label: 'Dropped catch' },
              { v: 'missed_runout', label: 'Missed run-out' },
              { v: 'misfield',      label: 'Misfield' },
            ].map(o => (
              <button
                key={o.v || 'none'}
                className={`sc-chip ${incident === o.v ? 'sc-active' : ''}`}
                onClick={() => { setIncident(o.v); if (!o.v) setIncidentPlayer(''); }}
              >
                {o.label}
              </button>
            ))}
          </div>
          {incident && (
            <>
              <h4>By which fielder?</h4>
              <select
                value={incidentPlayerId}
                onChange={(e) => setIncidentPlayer(e.target.value)}
                style={{ width: '100%', padding: 8, marginBottom: 8 }}
              >
                <option value="">— pick fielder —</option>
                {(bowlingSquad || []).map(s => (
                  <option key={s.member_id} value={s.member_id}>
                    {nameOf(s.member_id)}{s.is_sub ? ' (sub)' : ''}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Correction reason */}
          <h4>Why this edit? (optional)</h4>
          <input
            type="text"
            value={correctionReason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. mis-tapped 4 instead of 6"
            style={{
              width: '100%', padding: '10px 12px',
              border: '1px solid var(--sc-border-mid)', borderRadius: 8,
              fontSize: 13, fontFamily: 'inherit',
            }}
          />
        </div>

        <div className="sc-modal-actions">
          <button className="sc-submit" disabled={busy} onClick={handleSave}>
            {busy ? 'Saving…' : 'Save edit'}
          </button>
          <button className="sc-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
